import { ecolabelsApi } from "../api/ecolabelsApi";
import type { IrecDeviceLookupResponse } from "../api/ecolabelsTypes";
import {
  applyEvidentLookup,
  applyIrecIdentity,
  irecDeviceCode,
  type IrecLookupStatus,
  type IrecLookupTarget,
} from "./deviceUploadMapping";

// One register round-trip per code per page load: the same code is committed from the
// grid cell, re-committed from the panel, and re-emitted by chat turns.
const lookupsByCode = new Map<string, Promise<IrecDeviceLookupResponse>>();

/**
 * Look a row's I-REC code up in the public device register and fill its blank fields in
 * place. Resolves to the lookup status, or null when the row carries no I-REC identity.
 * Never throws: an unreachable register degrades to manual entry (`unavailable`).
 * Callers that show progress pass `onStatus`; it fires with `loading` first.
 */
export async function lookupAndFillIrecRow(
  row: IrecLookupTarget,
  onStatus?: (status: IrecLookupStatus | null) => void
): Promise<IrecLookupStatus | null> {
  if (!applyIrecIdentity(row)) {
    onStatus?.(null);
    return null;
  }
  const code = irecDeviceCode(row);
  if (code === null) {
    onStatus?.(null);
    return null;
  }
  onStatus?.("loading");
  const result = await fetchLookup(code);
  // The seller may have moved on while the register answered. A newer code has its own
  // lookup owning the status; no code at all means nobody else will clear the spinner.
  const currentCode = irecDeviceCode(row);
  if (currentCode !== code) {
    if (currentCode === null) onStatus?.(null);
    return null;
  }
  if (result.status === "found" && result.device) {
    applyEvidentLookup(row, result.device);
  }
  onStatus?.(result.status);
  return result.status;
}

/** Grid-row variant: the status lives on the row so the expansion panel can show it. */
export function enrichIrecRow(
  row: IrecLookupTarget & { irec_lookup: IrecLookupStatus | null }
): Promise<IrecLookupStatus | null> {
  return lookupAndFillIrecRow(row, (status) => {
    row.irec_lookup = status;
  });
}

/** Enrich many rows (a chat turn's extraction) a few at a time, so a 200-row spreadsheet
 * doesn't fire 200 register calls at once. */
export async function enrichIrecRows(
  rows: (IrecLookupTarget & { irec_lookup: IrecLookupStatus | null })[],
  concurrency = 3
): Promise<void> {
  const queue = rows.filter(
    (row) => row.irec_lookup === null && irecDeviceCode(row) !== null
  );
  const worker = async () => {
    for (let next = queue.shift(); next; next = queue.shift()) {
      await enrichIrecRow(next);
    }
  };
  await Promise.all(Array.from({ length: concurrency }, worker));
}

/** The hint an I-REC-identified form shows for a lookup status (shared by the field
 * panel and the add-device modal so the two can't drift). */
export function irecLookupHintKey(status: IrecLookupStatus | null): string {
  switch (status) {
    case "loading":
      return "ecolabels.upload.irec_lookup_loading";
    case "found":
      return "ecolabels.upload.irec_lookup_found";
    case "not_found":
      return "ecolabels.upload.irec_lookup_not_found";
    case "unavailable":
      return "ecolabels.upload.irec_lookup_unavailable";
    default:
      return "ecolabels.upload.irec_relaxed_hint";
  }
}

/** Test seam: forget cached answers between specs. */
export function resetIrecLookupCache(): void {
  lookupsByCode.clear();
}

function fetchLookup(code: string): Promise<IrecDeviceLookupResponse> {
  const cached = lookupsByCode.get(code);
  if (cached) return cached;
  const pending = ecolabelsApi
    .lookupIrecDevice(code)
    .then((response) => response.data)
    .catch((): IrecDeviceLookupResponse => {
      // A failed proxy call is not worth caching — the next commit may succeed.
      lookupsByCode.delete(code);
      return { status: "unavailable", device: null };
    });
  lookupsByCode.set(code, pending);
  return pending;
}
